import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import AsyncSelect from "react-select3/async";
import { customToast as toast } from "../../utils";
import CustomerService from "../../services/customerService";

const CustomerSelect = ({
  onChange,
  value,
  isLoading,
  isMulti,
  loadCustomers,
  setGlobalLoading,
  label,
  placeholder,
  disabled
}) => {
  const [selectLoading, setLoading] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const loadDefaultOptions = useCallback(async () => {
    try {
      if (!isMulti && value) {
        const customer = await CustomerService.getCustomer(value);
        setDefaultOptions([
          {
            value: customer._id,
            label: customer.name
          }
        ]);
      } else {
        const customers = await CustomerService.findByName("");
        setDefaultOptions(
          customers.map(({ _id, name }) => ({
            value: _id,
            label: name
          }))
        );
        if (loadCustomers) {
          loadCustomers(customers);
        }
      }
      setLoading(false);
      if (setGlobalLoading) {
        setGlobalLoading(false);
      }
    } catch (error) {
      toast.error();
    } finally {
      setLoading(false);
      if (setGlobalLoading) {
        setGlobalLoading(false);
      }
    }
  }, [isMulti, value, loadCustomers, setGlobalLoading]);

  useEffect(() => {
    if (defaultOptions.length === 0 && !selectLoading && !isLoading) {
      setLoading(true);
      if (setGlobalLoading) {
        setGlobalLoading(true);
      }
      loadDefaultOptions();
    }
  }, [
    loadDefaultOptions,
    selectLoading,
    defaultOptions,
    isLoading,
    setGlobalLoading
  ]);

  const loadingOptions = useCallback(
    async (inputValue = "") =>
      new Promise(async (resolve) => {
        try {
          setLoading(true);
          if (setGlobalLoading) {
            setGlobalLoading(true);
          }
          if (
            defaultOptions &&
            Array.isArray(defaultOptions) &&
            defaultOptions.length > 0
          ) {
            const arrayFiltered = defaultOptions.filter((i) =>
              i.label.toLowerCase().includes(inputValue.toLowerCase())
            );
            resolve(arrayFiltered);
          } else {
            const customers = await CustomerService.findByName(inputValue);
            resolve(
              customers.map(({ _id, name }) => ({
                value: _id,
                label: name
              }))
            );
          }
          setLoading(false);
          if (setGlobalLoading) {
            setGlobalLoading(false);
          }
        } catch (err) {
          toast.error();
        } finally {
          setLoading(false);
          if (setGlobalLoading) {
            setGlobalLoading(false);
          }
        }
      }),
    [defaultOptions, setGlobalLoading]
  );
  const initialValue =
    isMulti && Array.isArray(value) && defaultOptions.length
      ? defaultOptions.filter((x) =>
          value.find((i) => i === x.value || i.value === x.value)
        )
      : value;
  return (
    <React.Fragment>
      {label ? (
        <label
          className={`control-label ${!label ? "hidden" : ""}`}
          htmlFor="customer-select"
          style={{ textTransform: "uppercase" }}
        >
          {label}
        </label>
      ) : null}
      <AsyncSelect
        id="customer-select"
        name="customer-select"
        className="react-select-container"
        classNamePrefix="react-select-container"
        placeholder={placeholder}
        cacheOptions
        isLoading={selectLoading}
        loadOptions={loadingOptions}
        defaultOptions={defaultOptions}
        onChange={onChange}
        isMulti={isMulti}
        isDisabled={disabled}
        styles={{
          menu: (provided) => ({
            ...provided,
            zIndex: 999
          })
        }}
        value={initialValue}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length ? "No options" : "Start typing..."
        }
      />
    </React.Fragment>
  );
};

CustomerSelect.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.any,
  isMulti: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadCustomers: PropTypes.func,
  setGlobalLoading: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool
};

CustomerSelect.defaultProps = {
  isMulti: true,
  loadCustomers: null,
  setGlobalLoading: null,
  placeholder: "Customer",
  disabled: false
};

export default CustomerSelect;
